"use client";

import {
  Formatter,
  useChoreoGuide,
  useCMSBlogs,
  useCMSWriters,
  useMicroserviceShowVariant,
  useMobile,
  useScenicProjection,
} from "@with-nx/hooks-n-helpers";
import {
  DesignedCard,
  DesignedIcon,
  LoadingShimmer,
  ReusableSection,
  Rule,
} from "@with-nx/simple-ui/atoms";
import {
  AutomaticGetStarted,
  CatalogLayout,
  GenericContentHolder,
  GridBlog,
  GridStepScroller,
  ReusableGradientHero,
  ReusableSlantedContainer,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import moment from "moment";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { FaRegHourglass } from "react-icons/fa";
import { FiCalendar } from "react-icons/fi";
import { Box } from "simple-effing-primitive-layout";

const Editor = dynamic(() => import("../../components/w/Editor"), {
  ssr: false,
});

const ScenicProjectionBlogCTA = ({ id }: { id: string | number }) => {
  const scenicProjection = useScenicProjection({
    scenicProjectionId: id,
  });

  const variant = useMicroserviceShowVariant({
    slug: scenicProjection?.slug,
  });

  if (variant.loading) {
    return <LoadingShimmer parse="br:10 w:100% h:100" />;
  }

  return (
    <DesignedCard
      href={`/shows/${variant?.data?.showBrand?.slug}/${variant?.data?.slug}/scenic-projections`}
      size="16x9"
      inside={{
        large: variant?.data?.title,
        image: variant?.data?.attachments.find((item) => item?.name === "logo")
          ?.blob?.signedUrl,
        color:
          variant?.data?.logoColor ||
          variant?.data?.showBrand?.logoColor ||
          "#000000",
      }}
      outside={{
        title: variant?.data?.title,
        subtitle: "Scenic Projections",
      }}
    />
  );
};

const ChoreoGuideBlogCTA = ({ id }: { id: string | number }) => {
  const choreoGuide = useChoreoGuide({
    choreoGuideId: id,
  });

  const variant = useMicroserviceShowVariant({
    slug: choreoGuide?.slug,
  });

  if (variant.loading) {
    return <LoadingShimmer parse="br:10 w:100% h:100" />;
  }

  return (
    <DesignedCard
      href={`/shows/${variant?.data?.showBrand?.slug}/${variant?.data?.slug}/choreography-guides`}
      size="16x9"
      inside={{
        large: variant?.data?.title,
        image: variant?.data?.attachments.find((item) => item?.name === "logo")
          ?.blob?.signedUrl,
        color:
          variant?.data?.logoColor ||
          variant?.data?.showBrand?.logoColor ||
          "#000000",
      }}
      outside={{
        title: variant?.data?.title,
        subtitle: "Choreography Guides",
      }}
    />
  );
};

export default function Page({ resolvedUrl }) {
  const router = useRouter();
  const mobile = useMobile();
  const blogs = useCMSBlogs(
    {
      filter: {
        slug: [router.query.slug as string],
      },
    },
    typeof router.query.slug === "string"
  );
  const all = useCMSBlogs({
    page: 0,
    per: 3,
  });

  const blog = blogs.blogs.find((item) => item.slug === router.query.slug);

  const writers = useCMSWriters({});
  const writer = writers.writers.find((item) =>
    blog?.writerTags?.includes(item._id)
  );

  useEffect(() => {
    if (!blogs.loading && !blog?._id) {
      router.replace("/404");
    }
  }, [blogs.loading, blog?._id]);

  if (!blog) {
    return null;
  }

  const image = Formatter.image(blog.coverImage);

  const minutes = (() => {
    let words = 0;

    blog?.content?.blocks?.map((data) => {
      if (data?.data?.text && typeof data?.data?.text === "string") {
        words += data?.data?.text?.split(" ")?.length || 0;
      }
    });

    return Math.round(words / 150);
  })();

  return (
    <CatalogLayout
      transparentFixedNavBar
      title={
        blog?.metaTitle
          ? blog?.metaTitle
          : blog?.title
          ? `Broadway Media - Blog - ${blog.title}`
          : undefined
      }
      description={blog?.metaDescription || blog?.description}
      resolvedUrl={resolvedUrl}
    >
      <ReusableGradientHero
        slanted={false}
        src={
          blog.coverImage ? image : "/assets/hero/homepage-background-image.jpg"
        }
        backdrop="/assets/hero/homepage-gradient-webp.webp"
      />
      <ReusableSlantedContainer>
        <Box height={400} />
      </ReusableSlantedContainer>
      <Box
        parse={
          mobile
            ? "p:relative t:-600 i:2 mb:-600"
            : "p:relative t:-700 i:2 mb:-700"
        }
      >
        <div className="container mx-auto max-w-7xl">
          <Row gutter={[36, 18]} justify="center">
            <Col xs={24} sm={24} md={24} lg={21} xl={21}>
              <Box
                parse={mobile ? "pa:10 br:5 mb:24" : "pa:48 br:10 mb:24"}
                color="var(--font1)"
                border="1px solid rgba(255,255,255,0.1)"
              >
                <Rule
                  size={32}
                  weight="700"
                  parse="c:?background mb:12"
                  element="h1"
                >
                  {blog.title}
                </Rule>
                <Rule
                  size={20}
                  weight="500"
                  parse="d:block c:?foreground"
                  element="p"
                  bottom={24}
                >
                  {blog.description}
                </Rule>
                <Box css="d:flex a:center" parse="mb:24">
                  {writer ? (
                    <Box parse="d:flex a:center fw:wrap">
                      <Box parse="d:flex a:center pa:8 pr:16 c:#EBEBEB br:999 mr:16">
                        <Box css="br:999 oa:hidden" parse="w:24 h:24">
                          <GenericContentHolder
                            parse="w:32 h:32 br:999"
                            image={Formatter.image(writer.image)}
                            alt={writer.title || "Writer Image"}
                          />
                        </Box>
                        <Box parse="ml:6 d:flex a:center fw:wrap">
                          <Rule
                            size={14}
                            line={16}
                            rule="lt c:?background"
                            display="block"
                            weight="600"
                            color="var(--background)"
                          >
                            {writer.title}
                          </Rule>
                        </Box>
                      </Box>
                      <Box css="d:flex a:center">
                        <Box parse="ml:6 d:flex a:center fw:wrap">
                          <Rule
                            size={14}
                            line={16}
                            right={12}
                            rule="c:?entity"
                            display="block"
                            color="var(--entity)"
                            parse="d:inline-flex a:center"
                          >
                            <FiCalendar
                              size={16}
                              color="var(--entity)"
                              style={{ marginRight: 6 }}
                              title="calendar icon"
                            />
                            {moment(blog.date).format("MMMM Do, YYYY")}
                          </Rule>
                          {minutes ? (
                            <Rule
                              size={14}
                              line={16}
                              rule="lt c:?entity"
                              display="block"
                              color="var(--entity)"
                              parse="d:inline-flex a:center"
                            >
                              <FaRegHourglass
                                size={16}
                                color="var(--entity)"
                                style={{ marginRight: 6 }}
                                title="hour glass icon"
                              />
                              {`${minutes} min`}
                            </Rule>
                          ) : undefined}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Rule
                      size={14}
                      line={16}
                      right={12}
                      rule="c:?entity"
                      display="block"
                      color="var(--entity)"
                    >
                      {moment(blog.date).format("MMMM Do, YYYY")}
                    </Rule>
                  )}
                </Box>

                <div className="dark-mode-editor">
                  {Editor ? <Editor data={blog.content} /> : undefined}
                </div>

                <Box parse="mt:24 d:flex a:center j:center fd:column">
                  <Rule
                    size={24}
                    weight="700"
                    parse="c:?background mb:12"
                    element="h1"
                  >
                    Share with Theatre-Makers
                  </Rule>
                  <Box parse="d:flex a:center j:center">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=https://broadwaymedia.com/blog/${blog.slug}`}
                      target="_blank"
                      rel="noopener"
                      style={{ marginLeft: 12, marginRight: 12 }}
                    >
                      <DesignedIcon size={48} name="@facebook" />
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?text=${blog.title}&url=https://broadwaymedia.com/blog/${blog.slug}`}
                      target="_blank"
                      rel="noopener"
                      style={{ marginLeft: 12, marginRight: 12 }}
                    >
                      <DesignedIcon size={48} name="@twitter" />
                    </a>
                    <a
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=https://broadwaymedia.com/blog/${blog.slug}`}
                      target="_blank"
                      rel="noopener"
                      style={{ marginLeft: 12, marginRight: 12 }}
                    >
                      <DesignedIcon size={48} name="@linkedin" />
                    </a>
                  </Box>
                </Box>
              </Box>
              {blog.scenicProjectionTags?.length ||
              blog.choreoGuideTags?.length ? (
                <Row gutter={[36, 24]}>
                  <Col xs={24}>
                    <Rule parse="!ht d:block">Explore Our Catalog</Rule>
                  </Col>
                  {blog.scenicProjectionTags?.map((id) => (
                    <Col xs={24} sm={24} md={12} lg={8} key={id}>
                      <ScenicProjectionBlogCTA id={id} />
                    </Col>
                  ))}
                  {blog.choreoGuideTags?.map((id) => (
                    <Col xs={24} sm={24} md={12} lg={8} key={id}>
                      <ChoreoGuideBlogCTA id={id} />
                    </Col>
                  ))}
                </Row>
              ) : undefined}

              <Rule
                rule="ht"
                color="var(--font1)"
                display="block"
                bottom={24}
                top={24}
              >
                Continue Reading
              </Rule>

              <GridStepScroller
                inside
                xs={24}
                sm={24}
                md={12}
                lg={12}
                xl={12}
                items={all.blogs
                  ?.filter((i) => i.slug !== router.query?.slug)
                  .map((blog, b) => {
                    if (b > 3) {
                      return null;
                    }

                    return (
                      <GridBlog
                        key={blog._id}
                        date={blog.date}
                        description={blog.description}
                        image={Formatter.compressed(blog.coverImage, 256, true)}
                        title={blog.title}
                        url={`/blog/${blog.slug}`}
                        transparent={false}
                        properties={{
                          height: "100%",
                        }}
                      />
                    );
                  })}
              />

              {blog?.displayGetStartedForm ? (
                <ReusableSection top={96}>
                  <Rule parse="!hm d:block mb:24">Want to Learn More?</Rule>
                  <AutomaticGetStarted />
                </ReusableSection>
              ) : undefined}
            </Col>
          </Row>
        </div>
      </Box>
    </CatalogLayout>
  );
}

export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};
